/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import GridContainer from '../../components/Grid/GridContainer';
import { SimCardServiceTemplate } from '../../interfaces/platform/SimCardServiceTemplate';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import GridItem from '../../components/Grid/GridItem';
import CustomInputExt from '../../components/CustomInput/CustomInputExt';
import CardHeaderExt from '../../components/Heading/CardHeaderExt';
import { Checkbox, FormControlLabel, Typography } from '@material-ui/core';
import I18n from '../../components/Translate/I18n';
import CardBody from '../../components/Card/CardBody';
import Card from '../../components/Card/Card';
import { FeatureTypes, FeatureVerbType } from '../../modules/features/FeaturesTypes';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import { ComponentParam } from '../Features/component/ComponentByGroup';
import { ServiceTempateElementList } from '../Features/ServiceTempateElementList';
import { usePlatformResellerList } from '../../modules/lib/hooks/usePlatformResellerHooks';
import { VirtualComponentEditDiscount } from './VirtualComponentEditDiscount';
import PlatformResellerInterface from '../../interfaces/platform/PlatformResellerInterface';
import { ServicesNotActiveList } from '../Features/ServicesNotActiveList';
import { VirtualComponentSelectedPromotion } from './VirtualComponentSelectedPromotion';
import { ServiceTemplateItems } from '../Features/ServiceProfileEdit';

const _ = require('underscore');

type VirtualComponentEditType = {
    handleSetBrandText: (text: string) => void;
};

export const VirtualComponentEdit: FunctionComponent<VirtualComponentEditType> = ({ handleSetBrandText }): ReactElement => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [serviceTemplate, setServiceTemplate] = useState<SimCardServiceTemplate | null>(null);
    const [templateItems, setTemplateItems] = useState<ServiceTemplateItems>({ components: [] });
    const [templateName, setTemplateName] = useState('');
    const [templateActive, setTemplateActive] = useState<boolean>(false);
    const [platformReseller, setPlatformReseller] = useState<PlatformResellerInterface | null>(null);
    const [clipPrefix, setClipPrefix] = useState<string | null>(null);

    const listType = new FeatureTypes(process.env);
    const platformResellers = usePlatformResellerList();

    const getPlatformResellerName = (platformResellerId: number): string => {
        if (platformResellers) {
            for (let i = 0; i < platformResellers['hydra:member'].length; i++) {
                if (platformResellers['hydra:member'][i].id === platformResellerId) {
                    if (!platformReseller) {
                        setPlatformReseller(platformResellers['hydra:member'][i]);
                    }
                    return platformResellers['hydra:member'][i].mainname;
                }
            }
        }
        return '';
    };

    useEffect(() => {
        if (serviceTemplate === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getWithCallback(
                FormGeneralHelper.getByUri('/' + id),
                'sim_card_service_edit_item',
                (data: SimCardServiceTemplate) => {
                    setTemplateItems(JSON.parse(data.templateItems));
                    setTemplateName(data.templateName);
                    setServiceTemplate(data);
                    setTemplateActive(data.active);
                    setPlatformReseller(null);
                    setClipPrefix(data.clipPrefix);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    }, [dispatch, id, serviceTemplate]);

    const saveCallback = (): void => {
        if (serviceTemplate) {
            serviceTemplate.templateItems = JSON.stringify(templateItems);
            serviceTemplate.templateName = templateName;
            serviceTemplate.active = templateActive;
            serviceTemplate.clipPrefix = clipPrefix;
            serviceTemplate.ussdSmsCommunicationId = -1;
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.putWithCallback(
                FormGeneralHelper.getByUri(serviceTemplate['@id']),
                'sim_card_service_update_template_items',
                serviceTemplate,
                (data: SimCardServiceTemplate) => {
                    setTemplateItems(JSON.parse(data.templateItems));
                    setTemplateName(data.templateName);
                    setServiceTemplate(data);
                    setClipPrefix(data.clipPrefix);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    };

    const deletePromotionCallback = (promotionId: number): void => {
        const newItems: Array<{ value: number; name: string }> = [];
        if (templateItems.promotions) {
            for (let i = 0; i < templateItems.promotions.length; i++) {
                if (templateItems.promotions[i].value !== promotionId) {
                    newItems.push(templateItems.promotions[i]);
                }
            }
            setTemplateItems({ promotions: newItems, components: templateItems.components });
        }
    };

    const deleteCallback = (serviceName: string): void => {
        const newItems: Array<{ [key: string]: { verb: string; param: Array<{ name: string; value: string | number }> } }> = [];
        for (let i = 0; i < templateItems.components.length; i++) {
            const key = Object.keys(templateItems.components[i])[0];
            const values = templateItems.components[i][key];
            if (key !== serviceName) {
                newItems.push({ [key]: values });
            }
        }
        setTemplateItems({ components: newItems, promotions: templateItems.promotions });
    };

    const addPromotionCallback = (promotion: { value: number; name: string }): void => {
        const newItems = _.clone(templateItems);
        if (!newItems.promotions) {
            newItems.promotions = [];
        }
        newItems.promotions.push(promotion);
        setTemplateItems(newItems);
    };

    const addCallback = (componentParams: ComponentParam): void => {
        const newItems = _.clone(templateItems);
        let params: any = [];
        try {
            params = JSON.parse(componentParams.params);
        } catch (e) {
            console.log(e);
        }
        newItems.components.push({
            [componentParams.componentName]: {
                verb: componentParams.status === 1 ? FeatureVerbType.ACTIVATE : FeatureVerbType.DEACTIVATE,
                param: params,
            },
        });
        setTemplateItems(newItems);
    };

    const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        const field = event.target.name;
        switch (field) {
            case 'clipPrefix':
                setClipPrefix(value);
                break;
            default:
                setTemplateName(value);
        }
    };

    const onCheckBoxEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const name = event.target.name;
        const value = event.target.value;
        const checked = event.target.checked;
        setTemplateActive(checked);
    };

    if (serviceTemplate && !serviceTemplate.clip) {
        return <React.Fragment></React.Fragment>;
    }
    return (
        <GridContainer spacing={1}>
            <GridItem xs={12}>
                <Card>
                    <CardHeaderExt
                        additionalContent={
                            <React.Fragment>
                                <I18n trans="prov:features.edit_template_group_view" />
                            </React.Fragment>
                        }
                    />
                    <CardBody>
                        <GridContainer spacing={1}>
                            <GridItem xs={12}>
                                {serviceTemplate && platformResellers && (
                                    <Typography variant="subtitle1">
                                        <I18n
                                            trans="prov:features.platform_reseller_view"
                                            valueObject={{ partnerName: getPlatformResellerName(serviceTemplate.platformResellerId) }}
                                        />
                                    </Typography>
                                )}
                            </GridItem>
                            <GridItem xs={12}>
                                {serviceTemplate && (
                                    <Typography variant="subtitle2">
                                        <I18n trans="prov:features.template_id_group" valueObject={{ id: serviceTemplate.id }} />
                                    </Typography>
                                )}
                            </GridItem>
                            <GridItem xs={6}>
                                <CustomInputExt
                                    formControlProps={{ fullWidth: true }}
                                    required={false}
                                    labelText="prov:features.template_name_group"
                                    name="templateName"
                                    value={templateName}
                                    onChange={onChangeEvent}
                                />
                            </GridItem>
                            <GridItem xs={6}>
                                <CustomInputExt
                                    formControlProps={{ fullWidth: true }}
                                    required={true}
                                    labelText="prov:profile.prefix"
                                    name="clipPrefix"
                                    value={clipPrefix}
                                    onChange={onChangeEvent}
                                />
                            </GridItem>
                            <GridItem xs={6}>
                                <FormControlLabel
                                    control={<Checkbox checked={templateActive} name="template_active" onChange={onCheckBoxEvent} />}
                                    label={<I18n trans="prov:features.template_active_group" />}
                                />
                            </GridItem>
                            {serviceTemplate && (
                                <React.Fragment>
                                    <GridItem xs={12}>
                                        <Typography variant="subtitle2">
                                            <I18n trans="prov:features.template_positions_group" />
                                        </Typography>
                                    </GridItem>
                                    <GridItem xs={6}>
                                        <VirtualComponentSelectedPromotion serviceTemplate={templateItems} deleteCallback={deletePromotionCallback} />
                                        <ServiceTempateElementList serviceTemplate={templateItems} deleteCallback={deleteCallback} />
                                    </GridItem>
                                    <GridItem xs={6}>
                                        {platformReseller && (
                                            <VirtualComponentEditDiscount platformReseller={platformReseller} addCallback={addPromotionCallback} />
                                        )}
                                        <ServicesNotActiveList
                                            isVc={true}
                                            operatorTypes={listType.operatorType}
                                            addCallback={addCallback}
                                            platformResellerId={serviceTemplate.platformResellerId}
                                        />
                                    </GridItem>
                                </React.Fragment>
                            )}
                        </GridContainer>
                        <GridContainer>
                            <GridItem xs={12}>
                                <CustomTextButtom callback={saveCallback} callbackParam={{}} label="general:common.save" size="sm" />
                            </GridItem>
                        </GridContainer>
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};
